import React, {FC, memo, useState, useEffect, useRef} from "react";
import {DatePicker, Button, Tooltip} from "antd";
import moment, {Moment} from "moment";
import {LessonOfTheCourseType} from "./PreviewBooking";
import {
    getAllreadyConfirmedTimeRange,
    getAlreadyBookedDatesOfAttendee,
    getCourseLessonsOfTimeRange,
    getDateBackgroundColor,
    getDateRangeForAllCourseLessons,
    hasLessonOnDate,
} from "./DateRangePickerUtils";
import {blue, green, orange, red} from "@ant-design/colors";
import {GetHolidaysByCompanyAndDateRangeQuery} from "../../../generated/graphql";
import {lightenColor} from "../helper/holidayHelper";
import {CalendarOutlined} from "@ant-design/icons";
import {colorLessonBookingSecondary, colorLessonBookingTertiary, colorLessonBookingTextSecondary} from "../../../styles/colors";
import {DATE_RANGE_FORMATS} from "../../../views/DateInput/DateInput";

const {RangePicker} = DatePicker;

type DateRangePickerProps = {
    showPicker: boolean;
    setShowPicker: (showPicker: boolean) => void;
    selectedLessonIds: Array<React.Key>;
    setSelectedLessonIds: (selectedLessonIds: Array<React.Key>) => void;
    lessonsOfCourse: LessonOfTheCourseType;
    getHolidayInfo: (date: Moment) => GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"][number] | undefined;
    alreadyBookedLessonIdsWithoutCurrentBooking?: Array<string>;
    holidays?: GetHolidaysByCompanyAndDateRangeQuery["holidaysByCompanyAndDateRange"];
};

const DateRangePicker: FC<DateRangePickerProps> = ({
    selectedLessonIds,
    setSelectedLessonIds,
    lessonsOfCourse,
    getHolidayInfo,
    alreadyBookedLessonIdsWithoutCurrentBooking,
    holidays,
    showPicker,
    setShowPicker,
}) => {
    const [tempDateRange, setTempDateRange] = useState<[Moment | null, Moment | null]>([null, null]);
    const containerRef = useRef<HTMLDivElement>(null);
    const dateRangeForAllCourseLessons = getDateRangeForAllCourseLessons(lessonsOfCourse);
    const allreadyBookedDatesOfAttendee = getAlreadyBookedDatesOfAttendee(lessonsOfCourse, alreadyBookedLessonIdsWithoutCurrentBooking ?? []);

    const firstDateOfAllCourseLessons = dateRangeForAllCourseLessons[0];
    const lastDateOfAllCourseLessons = dateRangeForAllCourseLessons[dateRangeForAllCourseLessons.length - 1];

    const allreadyConfirmedTimeRange: [moment.Moment | null, moment.Moment | null] = getAllreadyConfirmedTimeRange(
        lessonsOfCourse,
        selectedLessonIds ? (selectedLessonIds as Array<string>) : undefined,
    );

    // Reset tempDateRange when picker is closed
    useEffect(() => {
        if (!showPicker) {
            setTempDateRange([null, null]);
        }
    }, [showPicker]);

    const handleConfirmDateRange = () => {
        let dateRangeToUse = tempDateRange;

        // Fallback: Wenn tempDateRange nicht gesetzt ist (z.B. bei manueller Eingabe ohne Blur),
        // lese die Werte direkt aus den DOM-Inputs INNERHALB dieses Containers
        if (!tempDateRange[0] || !tempDateRange[1]) {
            try {
                // Suche nur innerhalb unseres Container-Refs, um Konflikte mit anderen Pickern zu vermeiden
                const inputs = containerRef.current?.querySelectorAll(".ant-picker-input > input");

                if (inputs && inputs.length >= 2) {
                    const startDateStr = (inputs[0] as HTMLInputElement).value;
                    const endDateStr = (inputs[1] as HTMLInputElement).value;

                    if (startDateStr && endDateStr) {
                        const startDate = moment(startDateStr, "DD.MM.YYYY", true);
                        const endDate = moment(endDateStr, "DD.MM.YYYY", true);

                        if (startDate.isValid() && endDate.isValid()) {
                            dateRangeToUse = [startDate, endDate];
                        }
                    }
                }
            } catch (e) {
                console.error("Fehler beim Auslesen der Datumswerte:", e);
            }
        }

        if (dateRangeToUse[0] && dateRangeToUse[1]) {
            const courseLessonsOfTimeRange = getCourseLessonsOfTimeRange(lessonsOfCourse, dateRangeToUse);
            const onlyValidDates = courseLessonsOfTimeRange.filter((courseLesson) => {
                return !allreadyBookedDatesOfAttendee.some((date) => date.isSame(courseLesson.startDateTime, "day"));
            });
            const validLessonIds = onlyValidDates.map((courseLesson) => {
                return courseLesson.id as React.Key;
            });
            setSelectedLessonIds(validLessonIds);
            setShowPicker(false);
            setTempDateRange([null, null]);
        }
    };

    const handleCancel = () => {
        setShowPicker(false);
        setTempDateRange([null, null]);
    };

    if (!showPicker) {
        return (
            <Button
                type="default"
                // size="small"
                style={{width: "100%", marginBottom: "8px", backgroundColor: colorLessonBookingTertiary, border: "none", color: "white", fontWeight: 600}}
                onClick={() => setShowPicker(true)}
            >
                <CalendarOutlined style={{marginRight: "8px"}} /> Zeitraum wählen
            </Button>
        );
    }

    return (
        <div ref={containerRef} style={{marginBottom: "8px"}}>
            <style>
                {`
                    .ant-picker-cell-disabled {
                        pointer-events: auto !important;
                    }
                    .ant-picker-cell-disabled .ant-picker-cell-inner {
                        pointer-events: auto !important;
                    }
                `}
            </style>
            {allreadyBookedDatesOfAttendee.length > 0 && (
                <div
                    style={{
                        marginBottom: "8px",
                        padding: "8px",
                        backgroundColor: red[0],
                        border: `1px solid ${red[3]}`,
                        borderRadius: "4px",
                        fontSize: "12px",
                        color: red[7],
                    }}
                >
                    <strong>Hinweis:</strong> Rot markierte Termine sind bereits für diesen Teilnehmer gebucht und können nicht erneut ausgewählt werden.
                </div>
            )}
            <RangePicker
                allowEmpty={[false, false]}
                value={
                    tempDateRange[0] || tempDateRange[1]
                        ? tempDateRange
                        : allreadyConfirmedTimeRange[0] && allreadyConfirmedTimeRange[1]
                        ? allreadyConfirmedTimeRange
                        : [firstDateOfAllCourseLessons, null]
                }
                style={{width: "100%", marginBottom: "8px"}}
                format={DATE_RANGE_FORMATS}
                onCalendarChange={(values: [Moment | null, Moment | null] | null) => {
                    console.log("onCalendarChange values", values);
                    setTempDateRange(values || [null, null]);
                }}
                onChange={(values: [Moment | null, Moment | null] | null) => {
                    console.log("onChange values", values);
                    setTempDateRange(values || [null, null]);
                }}
                disabledDate={(current) => {
                    if (current) {
                        // return allreadyBookedDatesOfAttendee.some((date) => date.isSame(current, "day"));
                        if (allreadyBookedDatesOfAttendee.some((date) => date.isSame(current, "day"))) {
                            return true;
                        }

                        if (!moment(current).isBetween(dateRangeForAllCourseLessons[0], dateRangeForAllCourseLessons[1], "day", "[]")) {
                            return true;
                        }
                    }

                    return false;
                }}
                dateRender={(current) => {
                    if (current) {
                        const hasLesson = hasLessonOnDate(lessonsOfCourse, current);
                        const holiday = getHolidayInfo(current);
                        const backgroundColor = getDateBackgroundColor(current, lessonsOfCourse, holidays);
                        const isInBookedRange = current.isBetween(tempDateRange[0], tempDateRange[1], "day", "[]");
                        const isInConfirmedRange = current.isBetween(allreadyConfirmedTimeRange[0], allreadyConfirmedTimeRange[1], "day", "[]");
                        const isAlreadyBooked = allreadyBookedDatesOfAttendee.some((date) => date.isSame(current, "day"));

                        // Bestimme die finale Hintergrundfarbe
                        let finalBackgroundColor = backgroundColor;
                        if (isAlreadyBooked) {
                            finalBackgroundColor = red[2];
                        } else if (isInBookedRange) {
                            finalBackgroundColor = green[1];
                        } else if (isInConfirmedRange) {
                            finalBackgroundColor = orange[2];
                        }

                        const dateElement = (
                            <div
                                style={{
                                    position: "relative",
                                    height: "100%",
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    // backgroundColor,
                                    padding: hasLesson ? "3px" : "unset",
                                    borderRadius: hasLesson ? "20px" : "0px",
                                    textDecoration: holiday ? "underline" : "none",
                                    textDecorationColor: holiday ? backgroundColor : "transparent",
                                    textDecorationThickness: "2px",
                                    textUnderlineOffset: "2px",
                                    border: hasLesson ? `1px solid ${blue[4]}` : "none",
                                    fontWeight: hasLesson ? 600 : 400,
                                    color: hasLesson ? blue[9] : "inherit",
                                    zIndex: 1000,
                                    backgroundColor: finalBackgroundColor,
                                    cursor: isAlreadyBooked ? "not-allowed" : "pointer",
                                    pointerEvents: "auto",
                                }}
                            >
                                {current.format("D")}
                            </div>
                        );

                        if (isAlreadyBooked) {
                            return (
                                <Tooltip title="Dieser Termin ist bereits für diesen Teilnehmer gebucht" trigger="click" overlayStyle={{zIndex: 99999}}>
                                    {dateElement}
                                </Tooltip>
                            );
                        }

                        return dateElement;
                    }
                    return null;
                }}
                renderExtraFooter={() => (
                    <>
                        <Button
                            block
                            type="primary"
                            size="small"
                            style={{flex: 1, backgroundColor: colorLessonBookingTertiary, border: "none", color: "white" /* , fontWeight: 600 */}}
                            onClick={() => {
                                handleConfirmDateRange();
                                setShowPicker(false);
                            }}
                        >
                            Zeitraum übernehmen
                        </Button>
                    </>
                )}
                // defaultPickerValue={dateRangeForAllCourseLessons[0] ? [dateRangeForAllCourseLessons[0], dateRangeForAllCourseLessons[0]] : undefined}
            />
            <div style={{display: "flex", gap: "8px"}}>
                <Button
                    type="primary"
                    size="small"
                    style={{flex: 1, backgroundColor: colorLessonBookingTertiary, border: "none", color: "white" /* , fontWeight: 600 */}}
                    onClick={handleConfirmDateRange}
                    disabled={!tempDateRange[0] || !tempDateRange[1]}
                >
                    Zeitraum übernehmen
                </Button>
                <Button type="default" size="small" style={{flex: 1, borderColor: colorLessonBookingTertiary, color: colorLessonBookingTextSecondary}} onClick={handleCancel}>
                    Abbrechen
                </Button>
            </div>
        </div>
    );
};

export default memo(DateRangePicker);
